import React from "react";
import InputField from "../../components/InputField";
import DateField from "../DateField";
import Dropdown from "../Dropdown";
import MultipleSelectDropdown from "../MultipleSelectDropdown";
import ListValueField from "../ListValueField";
import SuggestInputField from "../SuggestInputField";
import AddressField from "./AddressField";
import CustomQuestionFields from "./CustomQuestionFields";
import AddOnLicensePicker from "./AddOnLicensePicker";
import GroupMembershipPicker from "./GroupMembershipPicker";
import AccountTypeDropdown from "./AccountTypeDropdown";
import HardwareChoice from "../hardware/HardwareChoice";
import HardwareOrderSection from "../hardware/HardwareOrderSection";
import ExistingComputerFields from "../hardware/ExistingComputerFields";
import { effectiveDialCode } from "./phoneNumber";
import { effectiveEmailDomain } from "./emailAddress";

/**
 * Marks a label as required so the orderer can tell required from optional fields
 * (PBI #291). The form renders a legend explaining the marker.
 */
const required = (label) => `${label} *`;

/**
 * Wraps a field so its validation message appears under it. Every validated field shows
 * its own message, so the orderer can see which field is wrong rather than only that
 * something is.
 */
const Field = ({ error, children }) => (
  <div className="w-full">
    {children}
    {error && <p className="text-sm text-error-600 mt-1">{error}</p>}
  </div>
);

// Same UTC-safe date shift as the pages use, so the datepicker's max attribute
// cannot drift a day in non-UTC timezones.
const shiftIsoDate = (isoDate, days) => {
  if (!isoDate) return "";

  const parsed = new Date(`${isoDate}T00:00:00Z`);
  if (Number.isNaN(parsed.getTime())) return "";

  parsed.setUTCDate(parsed.getUTCDate() + days);
  return parsed.toISOString().slice(0, 10);
};

const CreateUserForm = ({
  user,
  setUser,
  setHasChanges,
  billingGroups,
  accountTypes,
  onBillingGroupChange,
  onAccountTypeChange,
  onManagerChange,
  hardware,
  scoFormSettings,
  jobTitleOptions,
  officeOptions,
  countryOptions,
  companyOptions,
  onStartDateChange,
  onActivationDateChange,
  errors,
  userPrograms,
  users,
  addOnLicenses = [],
  groups = {},
  emailDomainOptions = [],
  departmentOptions = [],
  addresses = [],
  customQuestions = [],
  customAnswers = {},
  onCustomAnswerChange = () => {},
}) => {
  const billingGroupNames = billingGroups.map((group) => group.name);
  const managerNames = users.map((tenantUser) => tenantUser.displayName);

  const hasGroupOptions = Array.isArray(groups.options) && groups.options.length > 0;

  // #362: the datepicker blocks the dates validation would reject — the lead time
  // before the start date when the rule is enforced, otherwise just the start date.
  const activationDateMax = user.startDate
    ? shiftIsoDate(
        user.startDate,
        scoFormSettings.enforceActivationLeadTime &&
          Number.isFinite(scoFormSettings.activationLeadTimeDays)
          ? -scoFormSettings.activationLeadTimeDays
          : 0
      )
    : "";

  // #363: the account type decides the licence (assigned when the account is created), so
  // the form names it for the orderer and keeps it out of the add-on picker — it cannot be
  // deselected, and ordering it twice would make no sense.
  const selectedAccountType = (Array.isArray(accountTypes) ? accountTypes : []).find(
    (accountType) => accountType?.accountTypePublicId === user.accountTypePublicId
  );
  const includedLicenseMpn = selectedAccountType?.licenseMpn || "";
  const isIncludedLicense = (license) =>
    !!includedLicenseMpn &&
    license?.mpn?.toLowerCase() === includedLicenseMpn.toLowerCase();
  const includedLicenseName =
    addOnLicenses.find(isIncludedLicense)?.name || includedLicenseMpn;
  const addOnLicenseOptions = addOnLicenses.filter((license) => !isIncludedLicense(license));

  return (
    <>
      <p className="text-sm text-gray-600">
        Fields marked <span className="font-bold">*</span> are required.
      </p>

      <div className="grid grid-cols-2 gap-6 w-full">
        <div className="flex flex-col gap-4">
          <Field error={errors.firstName}>
            <InputField
              label={required("First name")}
              value={user.firstName}
              onChange={(e) => {
                setUser({ ...user, firstName: e.target.value });
                setHasChanges(true);
              }}
              placeholder="Enter first name"
              type="text"
            />
          </Field>

          <Field error={errors.lastName}>
            <InputField
              label={required("Last name")}
              value={user.lastName}
              onChange={(e) => {
                setUser({ ...user, lastName: e.target.value });
                setHasChanges(true);
              }}
              placeholder="Enter last name"
              type="text"
            />
          </Field>

          <ListValueField
            label="Title"
            value={user.jobTitle}
            options={jobTitleOptions}
            allowCustom={scoFormSettings.allowCustomJobTitle}
            onChange={(value) => {
              setUser({ ...user, jobTitle: value });
              setHasChanges(true);
            }}
            placeholder="Enter title"
          />

          {/* #357: Office, Company and Department offer the customer's values and take a
              new one, which is saved for the customer's next onboarding. */}
          <SuggestInputField
            label="Office"
            value={user.office}
            options={officeOptions}
            onChange={(value) => {
              setUser({ ...user, office: value });
              setHasChanges(true);
            }}
            placeholder="Choose or enter office"
          />

          <ListValueField
            label="Country"
            value={user.country}
            options={countryOptions}
            allowCustom={scoFormSettings.allowCustomCountry}
            onChange={(value) => {
              setUser({ ...user, country: value });
              setHasChanges(true);
            }}
            placeholder="Choose country"
          />

          <Field error={errors.address}>
            <AddressField
              addresses={addresses}
              user={user}
              onChange={(changes) => {
                setUser({ ...user, ...changes });
                setHasChanges(true);
              }}
            />
          </Field>

          {/* #359: the part before @ is typed, the domain is one of the customer's own. */}
          <Field error={errors.email}>
            <div className="flex gap-2 items-end w-full">
              <InputField
                label={required("Primary email address")}
                value={user.emailLocalPart || ""}
                onChange={(e) => {
                  setUser({ ...user, emailLocalPart: e.target.value });
                  setHasChanges(true);
                }}
                placeholder="firstname.lastname"
                type="text"
              />
              <span className="pb-2 text-gray-600">@</span>
              <div className="w-56 flex-none">
                <Dropdown
                  label="Domain"
                  value={effectiveEmailDomain(user.emailDomain, emailDomainOptions)}
                  options={emailDomainOptions}
                  onSelect={(domain) => {
                    setUser({ ...user, emailDomain: domain });
                    setHasChanges(true);
                  }}
                />
              </div>
            </div>
          </Field>

          {/* #360: each number has its own country code, pre-filled from Country. */}
          <Field error={errors.mobilePhone}>
            <div className="flex gap-2 items-end w-full">
              <div className="w-28 flex-none">
                <InputField
                  label="Country code"
                  value={effectiveDialCode(user.mobilePhoneCountryCode, user.country)}
                  onChange={(e) => {
                    setUser({ ...user, mobilePhoneCountryCode: e.target.value });
                    setHasChanges(true);
                  }}
                  placeholder="+46"
                  type="tel"
                />
              </div>
              <InputField
                label="Mobile Phone"
                value={user.mobilePhone}
                onChange={(e) => {
                  setUser({ ...user, mobilePhone: e.target.value });
                  setHasChanges(true);
                }}
                placeholder="733 12 34 56"
                type="tel"
              />
            </div>
          </Field>

          <Field error={errors.phone}>
            <div className="flex gap-2 items-end w-full">
              <div className="w-28 flex-none">
                <InputField
                  label="Country code"
                  value={effectiveDialCode(user.phoneCountryCode, user.country)}
                  onChange={(e) => {
                    setUser({ ...user, phoneCountryCode: e.target.value });
                    setHasChanges(true);
                  }}
                  placeholder="+46"
                  type="tel"
                />
              </div>
              <InputField
                label="Phone"
                value={user.phone}
                onChange={(e) => {
                  setUser({ ...user, phone: e.target.value });
                  setHasChanges(true);
                }}
                placeholder="42 12 34 56"
                type="tel"
              />
            </div>
          </Field>

          <SuggestInputField
            label="Company"
            value={user.company}
            options={companyOptions}
            onChange={(value) => {
              setUser({ ...user, company: value });
              setHasChanges(true);
            }}
            placeholder="Choose or enter company"
          />

          <SuggestInputField
            label="Department"
            value={user.department}
            options={departmentOptions}
            onChange={(value) => {
              setUser({ ...user, department: value });
              setHasChanges(true);
            }}
            placeholder="Choose or enter department"
          />

          {/* Närmsta chef listar enbart aktuella personer — by-tenant-frågan
              filtrerar bort borttagna användare (u.IsDeleted = 0). */}
          <Dropdown
            label="Manager"
            value={user.managerName || ""}
            options={managerNames}
            onSelect={onManagerChange}
            placeholder="Choose manager"
            searchable={true}
          />

          <Field error={errors.accountType}>
            <AccountTypeDropdown
              label={required("Account types")}
              accountTypes={accountTypes}
              selectedName={user.accountTypeName}
              selectedPublicId={user.accountTypePublicId}
              onSelect={onAccountTypeChange}
              placeholder="Choose account type"
            />
            {includedLicenseMpn && (
              <p className="text-sm text-gray-600 mt-1">
                Included licence: {includedLicenseName} — assigned automatically with the
                account type.
              </p>
            )}
          </Field>

          <Field error={errors.billingGroup}>
            <Dropdown
              label={required("Billing group")}
              value={user.billingGroupName || ""}
              options={billingGroupNames}
              onSelect={onBillingGroupChange}
              placeholder="Choose billing group"
              searchable={true}
            />
          </Field>

          {/* PBI #293: start date and activation date. The activation date is pre-filled
              to the configured lead time before the start date and may be moved earlier.
              Both show yyyy-mm-dd whatever the browser's language (#355). */}
          <Field error={errors.startDate}>
            <DateField
              label={required("Start date")}
              value={user.startDate || ""}
              onChange={onStartDateChange}
            />
          </Field>

          <Field error={errors.activationDate}>
            <DateField
              label={required("Activation date")}
              value={user.activationDate || ""}
              onChange={onActivationDateChange}
              max={activationDateMax || undefined}
            />
            {scoFormSettings.activationDateHelpText && (
              <p className="text-sm text-gray-600 mt-1">
                {scoFormSettings.activationDateHelpText}
              </p>
            )}
          </Field>

          <div className="w-full">
            <InputField
              label="Details"
              value={user.details || ""}
              onChange={(e) => {
                setUser({ ...user, details: e.target.value });
                setHasChanges(true);
              }}
              placeholder="Enter details"
              type="text"
            />
            <p className="text-sm text-gray-600 mt-1">
              Is there any additional information for Diflex regarding this
              onboarding? Please enter it here.
            </p>
          </div>
        </div>

        <div className="flex flex-col gap-4">
          {/* Feature #301: hardware choice, catalogue order and existing computer. The section
              is hidden when the API predates the catalogue, so nothing is posted that the mail
              could not resolve. */}
          {hardware.supported ? (
            <>
              <HardwareChoice value={hardware.choice} onChange={hardware.onChoiceChange} />

              {hardware.choice === "new" && (
                <HardwareOrderSection
                  products={hardware.products}
                  loading={hardware.productsLoading}
                  order={hardware.order}
                  onOrderChange={hardware.onOrderChange}
                />
              )}

              {hardware.choice === "existing" && (
                <ExistingComputerFields
                  devices={hardware.devices}
                  loading={hardware.devicesLoading}
                  value={hardware.existingComputer}
                  onChange={hardware.onExistingComputerChange}
                />
              )}

              {errors.hardware && (
                <p className="text-sm text-error-600">{errors.hardware}</p>
              )}
            </>
          ) : (
            <p className="text-sm text-gray-600">
              Hardware ordering is not available right now. Describe any hardware needs in the
              comment field.
            </p>
          )}

          {/* PBI #300: for both a new and an existing user. */}
          <AddOnLicensePicker
            options={addOnLicenseOptions}
            selected={user.addOnLicensePublicIds || []}
            onChange={(selectedIds) => {
              setUser({ ...user, addOnLicensePublicIds: selectedIds });
              setHasChanges(true);
            }}
          />

          {/* PBI #297: copy a colleague's Entra groups onto the new user. Read-only —
              Diflex assigns the membership by hand. */}
          <GroupMembershipPicker
            status={groups.status}
            asOfUtc={groups.asOfUtc}
            options={groups.options}
            selected={user.entraGroupPublicIds || []}
            onChange={(selectedIds) => {
              setUser({ ...user, entraGroupPublicIds: selectedIds });
              setHasChanges(true);
            }}
            userLabels={groups.userLabels}
            modelUserLabel={user.groupModelUserLabel || ""}
            onModelUserChange={groups.onModelUserChange}
            membershipsLoading={groups.membershipsLoading}
          />

          {/* #298: kept here rather than inside GroupMembershipPicker — the picker returns
              null when the group fetch fails, and the mailbox field must stay whatever the
              sync's status is. */}
          <div className="w-full">
            <InputField
              label="Shared mailboxes"
              value={user.sharedMailboxes || ""}
              onChange={(e) => {
                setUser({ ...user, sharedMailboxes: e.target.value });
                setHasChanges(true);
              }}
              placeholder="e.g. info@company.com, invoices@company.com"
              type="text"
              maxLength={512}
            />
            <p className="text-sm text-gray-600 mt-1">
              Mailboxes the user should have access to, separated by commas. Diflex grants
              the access manually — Insight does not change it.
            </p>
          </div>

          {/* #364: groups carry program access as well as permissions, so where the customer's
              groups can be shown they are the one place to ask for both. The agreement's user
              programs stay only as the fallback for a customer whose groups cannot be read. */}
          {!hasGroupOptions && (
            <MultipleSelectDropdown
              label="User programs"
              options={userPrograms.map((program) => ({
                label: program.displayName,
                value: program.displayName,
              }))}
              selected={(user.userPrograms || []).map((d) =>
                typeof d === "string" ? d : d.userProgramPublicId
              )}
              onChange={(selectedIds) => {
                setUser({
                  ...user,
                  userPrograms: selectedIds,
                });
                setHasChanges(true);
              }}
              placeholder="Choose user programs"
            />
          )}

          <InputField
            label="Comment"
            value={user.comment || ""}
            onChange={(e) => {
              setUser({ ...user, comment: e.target.value });
              setHasChanges(true);
            }}
            placeholder="Comment"
            type="text-area"
          />

          {/* PBI #320: the customer's own questions, configured per customer by Diflex. */}
          <CustomQuestionFields
            questions={customQuestions}
            answers={customAnswers}
            onAnswerChange={onCustomAnswerChange}
          />
        </div>
      </div>
    </>
  );
};

export default CreateUserForm;
